import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Loader2, CheckCircle2, AlertTriangle, ShieldAlert, TrendingUp, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface DominantStrategy {
  type: "clear_winner" | "conditional" | "trade_off" | "hidden_winner";
  option_name?: string;
  option_id?: string;
  explanation: string;
  key_insights: string[];
  action_steps?: string[];
  risks_to_watch?: string[];
}

interface EnrichedMetrics {
  alignment_score?: number;
  is_constraint_break?: boolean;
  is_strategy_shift?: boolean;
  total_capacity_percent?: number;
  effective_daily_exposure?: number;
  alignment_deductions?: string[];
}

interface MatrixScore {
  optionId: string;
  name: string;
  score: number;
  koFailed: boolean;
}

interface DominantStrategySummaryProps {
  dominantStrategy: DominantStrategy | null;
  isLoading?: boolean;
  metrics?: EnrichedMetrics | null;
  matrixScores?: MatrixScore[] | null;
  matrixDivergenceExplanation?: string | null;
}

export const DominantStrategySummary = ({ 
  dominantStrategy, 
  isLoading,
  metrics,
  matrixScores,
  matrixDivergenceExplanation,
}: DominantStrategySummaryProps) => {
  if (isLoading) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="py-6">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Analysiere...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!dominantStrategy) {
    return null;
  }

  const getStrategyBadge = () => {
    switch (dominantStrategy.type) {
      case "clear_winner":
        return { label: "Klarer Favorit", color: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" };
      case "conditional":
        return { label: "Bedingt", color: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" };
      case "trade_off":
        return { label: "Trade-Off", color: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20" };
      case "hidden_winner":
        return { label: "Überraschung", color: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" };
      default:
        return { label: "Analyse", color: "bg-muted text-muted-foreground border-muted" };
    }
  };

  const badge = getStrategyBadge();

  const getAlignmentColor = (score: number) => {
    if (score >= 80) return "text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10";
    if (score >= 50) return "text-amber-600 dark:text-amber-400 border-amber-500/30 bg-amber-500/10";
    return "text-destructive border-destructive/30 bg-destructive/10";
  };

  return (
    <Card className="border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-primary/10 shrink-0">
            <Trophy className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-3 flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {dominantStrategy.option_name ? (
                  <h3 className="text-lg font-semibold leading-tight">
                    <span className="text-primary">{dominantStrategy.option_name}</span> ist dominant
                  </h3>
                ) : (
                  <h3 className="text-lg font-semibold">Strategische Analyse</h3>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {/* Alignment Score Badge */}
                {metrics?.alignment_score != null && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className={cn("text-[10px] gap-1 font-mono", getAlignmentColor(metrics.alignment_score))}>
                          <TrendingUp className="h-2.5 w-2.5" />
                          {metrics.alignment_score}/100
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[250px]">
                        <p className="font-semibold text-xs mb-1">Alignment Score</p>
                        {metrics.alignment_deductions && metrics.alignment_deductions.length > 0 ? (
                          <ul className="text-xs space-y-0.5">
                            {metrics.alignment_deductions.map((d, i) => (
                              <li key={i} className="text-destructive">• {d}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-muted-foreground">Keine Abzüge – volle Alignment</p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <Badge variant="outline" className={cn("text-[10px]", badge.color)}>
                  {badge.label}
                </Badge>
              </div>
            </div>

            {/* Constraint Warnings */}
            {(metrics?.is_constraint_break || metrics?.is_strategy_shift) && (
              <div className="flex flex-wrap gap-2">
                {metrics.is_constraint_break && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-destructive/10 border border-destructive/20">
                    <AlertTriangle className="h-3 w-3 text-destructive" />
                    <span className="text-xs font-medium text-destructive">Deadline-Bruch</span>
                  </div>
                )}
                {metrics.is_strategy_shift && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <ShieldAlert className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Strategiewechsel</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Explanation */}
            <p className="text-sm text-muted-foreground leading-relaxed">
              {dominantStrategy.explanation}
            </p>

            {/* Metrics row */}
            {(metrics?.total_capacity_percent != null || metrics?.effective_daily_exposure != null) && (
              <div className="flex flex-wrap gap-3">
                {metrics.total_capacity_percent != null && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">Kapazität: </span>
                    <span className={cn("font-semibold", metrics.total_capacity_percent > 80 ? "text-destructive" : "text-foreground")}>
                      {metrics.total_capacity_percent}%
                    </span>
                  </div>
                )}
                {metrics.effective_daily_exposure != null && metrics.effective_daily_exposure > 0 && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">Eff. Burn/Tag: </span>
                    <span className="font-semibold text-foreground">€{metrics.effective_daily_exposure.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}

            {/* Matrix Divergence Info */}
            {(() => {
              if (!matrixScores || matrixScores.length === 0 || !dominantStrategy.option_name) return null;
              const topByScore = [...matrixScores].sort((a, b) => b.score - a.score)[0];
              const isDivergent = topByScore && topByScore.name !== dominantStrategy.option_name;
              if (!isDivergent) return null;
              return (
                <div className="rounded-lg border border-border overflow-hidden">
                  {/* Two-column leader comparison */}
                  <div className="grid grid-cols-2 divide-x divide-border">
                    <div className="p-3 bg-muted/30">
                      <div className="flex items-center gap-1.5 mb-1">
                        <TrendingUp className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Score Leader</span>
                      </div>
                      <p className="text-sm font-semibold text-foreground truncate">{topByScore.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{topByScore.score.toFixed(1)} Punkte</p>
                    </div>
                    <div className="p-3 bg-primary/5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Trophy className="h-3 w-3 text-primary" />
                        <span className="text-[10px] font-semibold text-primary uppercase tracking-wider">Empfohlene Strategie</span>
                      </div>
                      <p className="text-sm font-semibold text-primary truncate">{dominantStrategy.option_name}</p>
                      <p className="text-xs text-muted-foreground">Risiko + Alignment optimiert</p>
                    </div>
                  </div>
                  {/* Explanation */}
                  <div className="px-3 py-2.5 bg-accent/30 border-t border-border">
                    <div className="flex items-start gap-2">
                      <Info className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {matrixDivergenceExplanation || (
                          <>
                            Der höchste Matrix-Score allein bestimmt nicht die Empfehlung. Quentari berücksichtigt
                            zusätzlich Risikoprofil, Deadline-Konsistenz und strategisches Alignment.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Key Insights */}
            {dominantStrategy.key_insights.length > 0 && (
              <ul className="space-y-1">
                {dominantStrategy.key_insights.slice(0, 3).map((insight, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    <span className="text-foreground">{insight}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
